<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-magazine ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('magazin') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="saving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>{{ $t('eintrag_bearbeiten') }}: {{ record?.title }}</v-toolbar-title>
                </light-toolbar>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-slide-group next-icon="ic-20 ic-next ic-col-white" prev-icon="ic-20 ic-prev ic-col-white">
                        <v-btn v-if="!record?.is_published" color="primary" depressed @click="publishDlg = true">
                            <KpIcon icon-cls="upload" color="white" left />
                            {{ $t('publizieren') }}
                        </v-btn>
                        <v-btn v-if="record?.is_published" color="primary" depressed @click="unpublishDlg = true">
                            <KpIcon icon-cls="x" color="white" left />
                            {{ $t('unpublizieren') }}
                        </v-btn>

                        <span class="ma-1"> | </span>

                        <v-btn
                            :color="record?.to_newsletter ? 'primaryToggled' : 'primary'"
                            depressed
                            @click="toggleToNewsletter"
                        >
                            <KpIcon icon-cls="tonewsletter" color="white" left />
                            {{ $t('fuer_nl') }}
                        </v-btn>
                        <v-btn
                            :color="record?.is_featured ? 'primaryToggled' : 'primary'"
                            class="ml-1"
                            depressed
                            @click="toggleTipped"
                        >
                            <KpIcon icon-cls="typed" color="white" left />
                            {{ $t('getippt') }}
                        </v-btn>

                        <v-btn
                            :color="record?.is_top_thema ? 'primaryToggled' : 'primary'"
                            class="ml-1"
                            depressed
                            @click="publishTopThemaDlg = true"
                        >
                            <KpIcon icon-cls="star" color="white" left />
                            {{ $t('publish_top_thema') }}
                        </v-btn>

                        <span v-if="!hidePreview" class="ma-1"> | </span>

                        <v-btn
                            v-if="!hidePreview"
                            color="primary"
                            class="ml-1"
                            depressed
                            :href="getDetailPreviewUrl()"
                            target="tgk_preview"
                        >
                            <KpIcon icon-cls="preview" color="white" left />
                            {{ $t('prev_detail') }}
                        </v-btn>
                        <v-btn
                            v-if="!hidePreview"
                            color="primary"
                            class="ml-1"
                            depressed
                            :href="getSearchPreviewUrl()"
                            target="tgk_preview"
                        >
                            <KpIcon icon-cls="magazine" color="white" left />
                            {{ $t('prev_list') }}
                        </v-btn>
                    </v-slide-group>
                </v-toolbar>
            </div>

            <!-- Navigation -->
            <div class="navigation">
                <v-tabs v-model="activeItem" vertical background-color="transparent" hide-slider>
                    <v-tab
                        v-for="item in navItems"
                        :key="item.id"
                        :disabled="record?.isPhantom()"
                        exact
                        class="nav-tab"
                        :to="{ name: item.routeName }"
                    >
                        <KpIcon :icon-cls="`${item.icon}`" color="grey" active-color="black" class="ma-1" left />
                        {{ item.title }}
                    </v-tab>
                </v-tabs>

                <v-divider class="mt-5 mb-5" />

                <!-- Publish-Date display: -->
                <div v-if="record?.publish_from?.isValid()" class="tgk-paragraph-text">
                    <strong>{{ $t('publiziert_ab') }}:</strong><br />
                    <time class="dtstart" :datetime="record?.publish_from?.toISOStringIf()">{{
                        record?.publish_from?.formatIf('dd, DD.MM.YYYY')
                    }}</time>
                    <span v-if="record?.publish_to?.isValid()">
                        -
                        <time class="dtstart" :datetime="record?.publish_to?.toISOStringIf()">{{
                            record?.publish_to?.formatIf('dd, DD.MM.YYYY')
                        }}</time>
                    </span>
                </div>
                <div v-else class="tgk-paragraph-text">
                    {{ $t('nicht_publiziert') }}
                </div>
            </div>

            <!-- Main content -->
            <div class="content">
                <router-view
                    ref="activeChild"
                    v-model="isValid"
                    :record="record"
                    @saving="saving = $event"
                    @featured-image-created="onFeaturedImagesCreated"
                ></router-view>
            </div>

            <!-- footer -->
            <div class="bottom-toolbar d-flex justify-end">
                <PlainBtn class="mr-1" @click="onCancel">
                    {{ backRoute?.displayText ? $t('back_to', { text: backRoute?.displayText }) : $t('schliessen') }}
                </PlainBtn>
                <PlainBtn class="mr-1" :disabled="!isValid" @click="onBack">{{ $t('back') }}</PlainBtn>
                <ActionBtn :disabled="!isValid" class="mr-3" @click="onSave">
                    <KpIcon icon-cls="save" color="white" left />
                    {{ $t('save') }}
                </ActionBtn>

                <ActionBtn v-if="!lastItem" :disabled="!isValid" :loading="saving" @click="onSaveAndNext"
                    ><KpIcon icon-cls="save" color="white" left />{{ $t('save_next') }}</ActionBtn
                >
            </div>
        </v-card>
        <confirm-dialog
            v-model="unpublishDlg"
            :title="$t('unpublizieren')"
            :message="$t('unpublizieren_sure')"
            @confirm="onUnpublish"
        />

        <publish-dialog
            v-model="publishDlg"
            :title="$t('Publizieren')"
            :publish-from="publishFrom"
            :publish-to="publishTo"
            :loading="saving"
            @cancel="publishDlg = false"
            @publish="onPublish"
        ></publish-dialog>

        <publish-dialog
            v-model="publishTopThemaDlg"
            :title="$t('top_thema')"
            :publish-from="record ? record?.front_header_from : null"
            :publish-to="record ? record?.front_header_to : null"
            :loading="saving"
            @cancel="publishTopThemaDlg = false"
            @publish="onTopThemaPublish"
        ></publish-dialog>
    </div>
</template>

<script>
import { Magazin, MagazinCollection } from '@/models/Magazin';
import { errToStr, setBrowserTitle, shortenString } from '@/helpers/tools';
import { mapActions } from 'vuex';
import dayjs from 'dayjs';
import PublishDialog from '@/components/publish_dialog.vue';
import { frontendUrl, globalParam } from '@/helpers/globals';
import BackRouteMixin from '@/mixins/backroute';

export default {
    name: 'MagazinEdit',
    components: {
        PublishDialog,
    },
    mixins: [BackRouteMixin],

    async beforeRouteUpdate(to, from, next) {
        await this.onSave();
        if (to.params.id !== from.params.id) {
            this.record = null;
        }
        this.loadRecord(to.params.id);
        next();
    },

    data() {
        return {
            unpublishDlg: false,
            publishDlg: false,
            publishTopThemaDlg: false,
            publishFrom: dayjs(),
            publishTo: null,
            saving: false,
            record: null,
            isValid: true,
            activeItem: null,
            navItems: [
                {
                    id: 'basisdaten',
                    routeName: 'magazin-edit',
                    icon: 'allincpast',
                    title: this.$t('basisdaten'),
                },
                {
                    id: 'image',
                    routeName: 'magazin-edit-image',
                    icon: 'mediamanager',
                    title: this.$t('titelbild'),
                },
                {
                    id: 'content',
                    routeName: 'magazin-edit-content',
                    icon: 'list',
                    title: this.$t('inhaltsabschnitte'),
                },
                {
                    id: 'events',
                    routeName: 'magazin-edit-events',
                    icon: 'agenda',
                    title: this.$t('agenda'),
                },
                {
                    id: 'addresses',
                    routeName: 'magazin-edit-addresses',
                    icon: 'world',
                    title: this.$t('kulturverzeichnis'),
                },
                {
                    id: 'metadata',
                    routeName: 'magazin-metadata',
                    icon: 'info',
                    title: this.$t('metadata'),
                },
            ],
        };
    },

    computed: {
        activeNavItem() {
            const actRouteName = this.$route.name;
            const items = this.navItems.filter((navItem) => navItem.routeName === actRouteName);
            if (items?.length > 0) {
                return items[0];
            }
            return null;
        },
        lastItem() {
            const routeName = this.$route.name;
            return this.navItems[this.navItems.length - 1].routeName === routeName;
        },
        browserTitle() {
            const parts = [];
            if (this.record?.title) {
                parts.push(shortenString(this.record?.title, 30), this.activeNavItem?.title);
            } else {
                parts.push(this.$t('create_entry'));
            }
            parts.push(this.$t('magazin'));
            return parts.filter(Boolean).join(' - ');
        },
        hidePreview() {
            return globalParam('magazin.hide_preview');
        },
    },

    watch: {
        browserTitle: {
            handler(newValue) {
                setBrowserTitle(newValue);
            },
            immediate: true,
        },
    },

    async beforeDestroy() {
        if (!this.record?.isPhantom()) {
            await this.onSave();
        }
    },

    mounted() {
        this.loadRecord(this.$route.params.id);
        this.updateActiveNavItem(this.$route);
    },

    methods: {
        ...mapActions(['runAsync']),
        async loadRecord(id) {
            // create a new record if requested, or load an existing one:
            if (id === 'new' || !Number(id)) {
                this.record = new Magazin({
                    is_draft: true,
                });
                // premature return in case of new record:
                return;
            }

            // load existing record:
            this.record = null;
            try {
                const collection = new MagazinCollection();
                await this.runAsync(() => collection.query({ id: id }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        async onSave() {
            if (!this.record) {
                return;
            }

            this.saving = true;

            // give the child a change to do things before we save:
            // e.g. save block contents:
            if (this.$refs.activeChild.beforeSave) {
                const res = await this.$refs.activeChild.beforeSave();
                if (res === false) {
                    this.saving = false;
                    return false;
                }
            }

            // nothing to do, so don't save:
            if (!this.record?.changed()) {
                this.saving = false;
                return;
            }

            try {
                const isNew = !this.record?.id;
                await this.runAsync(() => this.record?.save());
                if (isNew) {
                    // when a new rec is created, replace the route...
                    this.$router.replace({ name: 'magazin-edit', params: { id: this.record?.id } });
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },

        async onSaveAndNext() {
            const res = await this.onSave();
            if (res === false) {
                return;
            }

            if (this.activeNavItem) {
                const nextIndex = this.navItems.indexOf(this.activeNavItem) + 1;
                if (nextIndex > 0 && this.navItems.length > nextIndex) {
                    this.$router.push({ name: this.navItems[nextIndex].routeName });
                }
            }
        },

        onCancel() {
            if (this.getBackRouteConfig()?.route) {
                this.$router.push(this.backRoute.route);
            } else {
                this.$router.push({ name: 'magazin' });
            }
        },

        async onBack() {
            await this.onSave();

            if (this.activeNavItem) {
                const nextIndex = this.navItems.indexOf(this.activeNavItem) - 1;
                if (nextIndex >= 0) {
                    this.$router.push({ name: this.navItems[nextIndex].routeName });
                }
            }
        },

        async onUnpublish() {
            if (this.record) {
                this.record?.set('is_draft', true);
                this.record?.set('publish_from', null);
                this.record?.set('publish_to', null);
                await this.onSave();
            }
        },

        async onPublish({ publishFrom, publishTo }) {
            if (this.record) {
                const fromDate = dayjs(publishFrom);
                const toDate = dayjs(publishTo);
                this.record?.set('is_draft', false);
                this.record?.set('publish_from', fromDate);
                this.record?.set('publish_to', toDate);
                await this.onSave();
                this.publishDlg = null;
            }
        },

        async onTopThemaPublish({ publishFrom, publishTo }) {
            if (this.record) {
                const fromDate = dayjs(publishFrom);
                const toDate = dayjs(publishTo);
                this.record?.set('front_header_from', fromDate);
                this.record?.set('front_header_to', toDate);
                await this.onSave();
                this.publishTopThemaDlg = null;
            }
        },

        async toggleToNewsletter() {
            if (this.record) {
                this.record?.set('to_newsletter', !this.record?.to_newsletter);
                await this.onSave();
            }
        },

        async toggleTipped() {
            if (this.record) {
                this.record?.set('is_featured', !this.record?.is_featured);
                await this.onSave();
            }
        },

        getDetailPreviewUrl() {
            const id = this.record ? this.record?.id : null;
            return frontendUrl(`/magazin/${id}`, { preview: 'enable' });
        },

        getSearchPreviewUrl() {
            const id = this.record ? this.record?.id : null;
            return frontendUrl(`/magazin/?preview=enable&id=${id}#id=${id}`);
        },

        onFeaturedImagesCreated() {
            this.onSave();
        },

        updateActiveNavItem(route) {
            if (route) {
                this.activeItem = this.getActNavItemIndex(route);
            }
        },

        getActNavItemIndex(route) {
            const routeName = route.name;
            for (const i in this.navItems) {
                if (this.navItems[i].routeName === routeName) {
                    return i;
                }
            }
            return 0;
        },

        getBrowserTitle() {
            return this.browserTitle;
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 200px 1fr;
    grid-template-areas:
        'header header'
        'nav content'
        'nav bottom';
}

.top-header {
    grid-area: header;
}
.navigation {
    grid-area: nav;
    background-color: var(--toolbar-bg-color);
    padding: 12px;
    border-right: 1px solid var(--gray-border-color);
}
.content {
    grid-area: content;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    overflow: hidden;
}

.bottom-toolbar {
    grid-area: bottom;
    background-color: var(--toolbar-bg-color);
    padding: 10px 27px;
}

.nav-tab {
    height: initial !important;
    justify-content: start !important;
    padding: 5px 3px;
    margin: 3px;
    text-transform: none !important;
    font-weight: normal;
    font-size: 1rem;
    letter-spacing: 0;
    &.v-tab--active {
        color: var(--nearblack-color) !important;
    }
}
</style>
